import { useEffect, useMemo, useReducer } from "react"
import PropTypes from "prop-types"
import { find, map, keyBy, capitalize } from "lodash"
import { Checkbox, List, ListItemText, Grid, Divider, Alert } from "@mui/material"
import { FEATURE } from "components/feature-permissions/shapes"
import { computePermissions } from "components/feature-permissions/helpers"
import OPTooltip from "components/material/tooltip"
import { InfoOutlined } from "@mui/icons-material"
import pluralize from "pluralize"

import "../styles.scss"

export default function FeaturePermissionsList({ accountFeatures, entityFeatures, features, entityLabel, onChange }) {
  const accountFeaturesById = useMemo(() => keyBy(accountFeatures, "id"), [accountFeatures])

  const [checkboxes, dispatch] = useReducer((state, action) => {
    switch (action.type) {
      case "update":
        return { ...state, [action.featureId]: action.checked }
      case "init":
        return computePermissions(action.features, action.entityFeatures)
      default:
        return state
    }
  }, {})

  useEffect(() => {
    dispatch({ type: "init", entityFeatures, features })
  }, [entityFeatures, features])

  function handleOnClick(featureId, checked) {
    return () => {
      dispatch({ type: "update", featureId, checked })
      onChange(featureId, checked)
    }
  }

  return (
    <div className="component_feature-permissions">
      <Alert severity="info">
        Feature access can be provided for an entire account on the superuser admin screen
      </Alert>
      <Grid container>
        <Grid className="header" container>
          <Grid size={2}>
            <OPTooltip title="Account-level permissions provide feature access to all users on the account, regardless of whether member-level permissions are provided">
              <span>Account <InfoOutlined /></span>
            </OPTooltip>
          </Grid>
          <Grid size={2}>
            <OPTooltip title={`${capitalize(entityLabel)}-level permissions provide feature access only to the selected ${entityLabel}, regardless of whether account-level permissions are provided`}>
              <span>{capitalize(pluralize(entityLabel))} <InfoOutlined /></span>
            </OPTooltip>
          </Grid>
          <Grid size={8}>Restricted feature</Grid>
        </Grid>
      </Grid>
      <Divider />
      <List className="component_feature-permissions-list">
        {map(checkboxes, (checked, featureId) => {
          const feature = find(features, { id: featureId })
          return (
            <Grid container className="feature" id={featureId} key={featureId} role="button" dense onClick={handleOnClick(featureId, !checked)}>
              <Grid size={2}>
                <Checkbox
                  name={`account-${featureId}`}
                  edge="start"
                  checked={Boolean(accountFeaturesById[featureId])}
                  tabIndex={-1}
                  disableRipple
                  color="primary"
                  disabled
                />
              </Grid>
              <Grid size={2}>
                <Checkbox
                  name={featureId}
                  edge="start"
                  checked={checked}
                  tabIndex={-1}
                  disableRipple
                  color="primary"
                />
              </Grid>
              <Grid size={8}>
                <ListItemText id={featureId} primary={feature.name} secondary={feature.description} />
              </Grid>
            </Grid>
          )
        })}
      </List>
    </div>
  )
}

FeaturePermissionsList.propTypes = {
  accountFeatures: PropTypes.arrayOf(FEATURE),
  entityFeatures: PropTypes.arrayOf(FEATURE),
  entityLabel: PropTypes.string,
  features: PropTypes.arrayOf(FEATURE),
  onChange: PropTypes.func.isRequired,
}
